import { StationLabel } from '@app/modules/admin/screens/stations/list/StationLabel';
import { auditReports } from '@mi-front/api-clients';
import { Seconds } from '@mi-front/core';
import { BtnLink, Duration } from '@mi-front/ui';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, Tooltip } from '@mui/material';
import get from 'lodash/get';
import { MRT_ColumnDef, MRT_Row } from 'material-react-table';
import moment from 'moment';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { LDateTime } from '../../../../../../../../../components/date/LDateTime';
import { useStationsFinder } from '../../../../../../../../../core/stations/hooks/useStations';
import { buildLoggerStreamUrl } from '../../../../../../../../logger/screens/stream/helpers/build-logger-stream-url';
import {
  gipReportResultDateTimeColumnHeaderMessage,
  gipReportResultMatchDateTimeColumnHeaderMessage,
} from '../../../../common/gip-report-result-intl-messages';

/** Row type for GIP report tables: schedule (unmatched/unprocessed/future) or matched schedule. */
type GipReportScheduleRow =
  | auditReports.GipReportScheduleDto
  | auditReports.GipReportMatchedScheduleDto;

type ColumnDefinition = MRT_ColumnDef<GipReportScheduleRow, unknown>;

interface GipColumns {
  materialStatus: ColumnDefinition;
  station: ColumnDefinition;
  /** Planned schedule datetime (`row.datetime`). */
  dateTime: ColumnDefinition;
  /** Detected match datetime (`row.match.datetime`) when the row is a matched schedule. */
  matchDateTime: ColumnDefinition;
  advertiser: ColumnDefinition;
  campaign: ColumnDefinition;
  version: ColumnDefinition;
  customId: ColumnDefinition;
  contract: ColumnDefinition;
  order: ColumnDefinition;
  duration: ColumnDefinition;
  actions: ColumnDefinition;
  offToleranceIndicator: ColumnDefinition;
}

export const useGipResultColumnsFactory = (): GipColumns => {
  const { $t } = useIntl();
  const finder = useStationsFinder();

  return useMemo(
    () => ({
      // Material status column - shows if audio material is present or missing
      materialStatus: {
        id: 'material-status',
        header: $t({
          id: 'audit.gipReport.columnMaterial',
          defaultMessage: 'Material',
        }),
        size: 140,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }: { row: MRT_Row<GipReportScheduleRow> }) => {
          const { audioId, customId } = row.original;
          const isPresent = !!audioId;

          if (isPresent) {
            return <div>{customId}</div>;
          }

          return (
            <Tooltip
              title={$t(
                {
                  id: 'audit.gipReport.result.material.missing',
                  defaultMessage: 'Material {customId} faltante',
                },
                { customId },
              )}
            >
              <div>
                <WarningIcon color="warning" /> {customId}
              </div>
            </Tooltip>
          );
        },
      },

      // Station column - avatar + name (same pattern as logger issues table)
      station: {
        id: 'station',
        header: $t({
          id: 'audit.gipReport.result.station',
          defaultMessage: 'Emisora',
        }),
        size: 200,
        accessorFn: (item: GipReportScheduleRow) => {
          return finder(item.stationId)?.name ?? item.stationId;
        },
        Cell: ({ row }: { row: MRT_Row<GipReportScheduleRow> }) => {
          const stationId = row.original.stationId;
          const station = finder(stationId) ?? { id: stationId, name: stationId, slug: stationId };
          return <StationLabel station={station} size="sm" singleLineEllipsis />;
        },
      },

      // Planned schedule datetime (station timezone)
      dateTime: {
        header: $t(gipReportResultDateTimeColumnHeaderMessage),
        size: 200,
        id: 'datetime',
        accessorFn: (item: GipReportScheduleRow) => item.datetime,
        Cell: ({ row }: { row: MRT_Row<GipReportScheduleRow> }) => {
          const station = finder(row.original.stationId);
          return <LDateTime value={row.original.datetime} showSeconds tz={station?.timezone} />;
        },
      },

      // Match / detected airing datetime (matched schedules only; station timezone)
      matchDateTime: {
        header: $t(gipReportResultMatchDateTimeColumnHeaderMessage),
        size: 200,
        id: 'match-datetime',
        accessorFn: (item: GipReportScheduleRow) =>
          'match' in item ? item.match?.datetime : undefined,
        Cell: ({ row }: { row: MRT_Row<GipReportScheduleRow> }) => {
          const station = finder(row.original.stationId);
          const matchDatetime = 'match' in row.original ? row.original.match?.datetime : undefined;
          if (!matchDatetime) {
            return null;
          }
          return <LDateTime value={matchDatetime} showSeconds tz={station?.timezone} />;
        },
      },

      // Advertiser column
      advertiser: {
        header: $t({
          id: 'audit.gipReport.result.advertiser',
          defaultMessage: 'Anunciante',
        }),
        accessorKey: 'advertiser',
        size: 200,
      },

      // Campaign column
      campaign: {
        header: $t({
          id: 'audit.gipReport.result.campaign',
          defaultMessage: 'Campaña',
        }),
        accessorKey: 'campaign',
        size: 200,
      },

      // Version column — long values: ellipsis in cell, full string via native `title` on hover
      version: {
        header: $t({
          id: 'audit.gipReport.result.version',
          defaultMessage: 'Versión',
        }),
        accessorKey: 'version',
        size: 200,
        Cell: ({ row }: { row: MRT_Row<GipReportScheduleRow> }) => {
          const raw = row.original.version;
          const text = raw != null && raw !== '' ? String(raw) : '';
          return (
            <Box component="span" title={text || undefined}>
              {text}
            </Box>
          );
        },
      },

      // Custom ID value (labeled as material in Spanish UI)
      customId: {
        id: 'customId',
        header: $t({
          id: 'audit.gipReport.columnCustomId',
          defaultMessage: 'Material',
        }),
        accessorKey: 'customId',
        size: 140,
      },

      // Contract column
      contract: {
        header: $t({
          id: 'audit.gipReport.result.contract',
          defaultMessage: 'Contrato',
        }),
        size: 140,
        accessorKey: 'custom.nocontratonacional',
      },

      // Order column
      order: {
        header: $t({
          id: 'audit.gipReport.result.order',
          defaultMessage: 'Orden',
        }),
        accessorKey: 'order',
        size: 140,
      },

      // Duration column - only for matched schedules
      duration: {
        header: $t({
          id: 'audit.gipReport.result.duration',
          defaultMessage: 'Duración',
        }),
        size: 150,
        accessorFn: (item: GipReportScheduleRow) => get(item, 'audio.duration', 0) as number,
        Cell: ({ renderedCellValue }: { renderedCellValue: unknown }) => {
          return <Duration seconds={((renderedCellValue as number) / 1000) as Seconds} />;
        },
      },

      // Actions column - show button to logger
      actions: {
        header: $t({
          id: 'common.text.actions',
          defaultMessage: 'Acciones',
        }),
        enableSorting: false,
        enableColumnFilter: false,
        accessorFn: (e: GipReportScheduleRow) => {
          const datetime = 'match' in e && e.match?.datetime ? e.match.datetime : e.datetime;
          const durationMs = get(e, 'audio.duration', 0) as number;
          const fromMs = moment(datetime).subtract(10, 'seconds').valueOf();
          const toMs = fromMs + (durationMs > 0 ? durationMs : 120 * 1000);
          const stationSlug = finder(e.stationId)?.slug ?? e.stationId;
          const url = buildLoggerStreamUrl(stationSlug, {
            selection: { from: fromMs, to: toMs },
          });
          return (
            <BtnLink size="xs" to={url} openInNewTab icon={<OpenInNewIcon />} className="mr-2">
              {$t({
                id: 'common.show',
                defaultMessage: 'Mostrar',
              })}
            </BtnLink>
          );
        },
      },

      // Off tolerance indicator - only for matched schedules
      offToleranceIndicator: {
        header: $t({
          id: 'audit.gipReport.result.schedule',
          defaultMessage: 'Programación',
        }),
        id: 'off-tolerance-indicator',
        accessorFn: (e: GipReportScheduleRow) => {
          const match = 'match' in e ? e.match : undefined;

          return match?.matchedScheduleConfirmationType === 'OFF_TOLERANCE' ? 'NO' : 'SI';
        },
      },
    }),
    [$t, finder],
  );
};
